import {
  AthenaEvent,
  AthenaEventType,
} from "@evidenceb/athena-event-storage-schemas";
import last from "lodash/last.js";
import sortBy from "lodash/sortBy.js";
import {
  MangoQuerySelector,
  RxCollection,
  RxDatabase,
  RxReplicationWriteToMasterRow,
  addRxPlugin,
  createRxDatabase,
} from "rxdb";
import { RxDBDevModePlugin, disableWarnings } from "rxdb/plugins/dev-mode";
import { RxDBLeaderElectionPlugin } from "rxdb/plugins/leader-election";
import { RxDBLocalDocumentsPlugin } from "rxdb/plugins/local-documents";
import { RxDBMigrationPlugin } from "rxdb/plugins/migration-schema";
import {
  RxReplicationState,
  replicateRxCollection,
} from "rxdb/plugins/replication";
import { getRxStorageDexie } from "rxdb/plugins/storage-dexie";
import { getRxStorageMemory } from "rxdb/plugins/storage-memory";
import {
  Observable,
  Subscription,
  filter,
  firstValueFrom,
  interval,
  map,
  mergeMap,
} from "rxjs";
import { AthenaEventSaveResult } from "../EventStorageClient/AthenaEventSaveResult.js";
import { EventStorageClient } from "../EventStorageClient/EventStorageClient.js";
import { ISO8601String } from "../utils/ISO8601String.js";
import { Milliseconds } from "../utils/Milliseconds.js";
import { athenaEventSchemaV0 } from "./athenaEventSchema.js";
import {
  AthenaEventStreamNotStarted,
  ReplicationPullError,
  ReplicationPushNonRetryableError,
  ReplicationPushRetryableError,
  ReplicationPushWrongBatchSizeError,
  SubscriberAlreadyRegistered,
  SubscriberNotRegistered,
  SubscriberProcessAthenaEventError,
} from "./errors.js";
import { LastSyncInfo } from "./LastSyncInfo.js";
import {
  RxdbAthenaEvent,
  fromAthenaEvent,
  toAthenaEvent,
} from "./RxdbAthenaEvent.js";
import { Subscriber } from "./Subscriber.js";

if (process.env["NODE_ENV"] !== "production") {
  if (process.env["NODE_ENV"] === "test") {
    disableWarnings();
  }
  addRxPlugin(RxDBDevModePlugin);
}
addRxPlugin(RxDBLeaderElectionPlugin);
addRxPlugin(RxDBMigrationPlugin);
addRxPlugin(RxDBLocalDocumentsPlugin);

export type AthenaEventsCollection = RxCollection<RxdbAthenaEvent>;

export type Database = RxDatabase<{ athena_events: AthenaEventsCollection }>;

export type ReplicationCheckpoint = {
  id: string;
  insertedAt: ISO8601String;
};

/**
 * Entity responsible for orchestrating the flow of `AthenaEvent`s from
 * publishers to subscribers.
 *
 * Publishers are those components that _create_ `AthenaEvent`s, by calling the
 * `AthenaEventStream.publish` method.
 *
 * Subscribers are those components that _receive and process_ `AthenaEvent`s.
 * Subscribers are "hooked" to `AthenaEventStream` with the
 * `AthenaEventStream.registerSubscriber` method.
 */
export class AthenaEventStream {
  private insertSubscriptionsBySubscriberId = new Map<string, Subscription>();
  private rxdb: Database | null = null;

  /**
   * DO NOT USE: RxDB instance, exposed for diagnostic. Null when the
   * `AthenaEventStream` instance has not been started.
   */
  public __rxdb: Database | null = null;
  /**
   * DO NOT USE: State of the RxDB replication, exposed for diagnostic. Null
   * when the `AthenaEventStream` instance has not been started.
   */
  public __replicationState: RxReplicationState<
    RxdbAthenaEvent,
    ReplicationCheckpoint
  > | null = null;

  constructor(
    private eventStorageClient: EventStorageClient,
    /** Id of the logged in user. */
    private userId: string,
    /** Hooks to be notified when notable events (e.g., errors) occur. */
    private hooks?: {
      onSubscriberProcessAthenaEventError?: (
        error: SubscriberProcessAthenaEventError,
      ) => void;
      onReplicationPushWrongBatchSizeError?: (
        error: ReplicationPushWrongBatchSizeError,
      ) => void;
      onReplicationPushRetryableError?: (
        error: ReplicationPushRetryableError,
      ) => void;
      onReplicationPushNonRetryableError?: (
        error: ReplicationPushNonRetryableError,
      ) => void;
      onReplicationPullError?: (error: ReplicationPullError) => void;
    },
    /**
     * List of `AthenaEventType`s to pull from the event-storage service. Since
     * many `AthenaEvent`s are just for tracking what the user does, and the
     * frontend is not interested in keeping them around, we're probably only
     * interested in "syncing back" a subset of `AthenaEvent`s.
     */
    private pullAthenaEventTypes?: AthenaEventType[],
    /**
     * Interval at which to pull from the event-storage service.
     * @default 30_000
     */
    private pullInterval: Milliseconds = 30_000,
    /**
     * Time after which to retry a failed pull or push.
     * @default 5_000
     */
    private retryFailedPushOrPullAfter: Milliseconds = 5_000,
  ) {}

  async start() {
    await this.createDatabase();
    await this.startReplication();
  }

  async stop() {
    this.ensureStarted(this.rxdb);
    await this.rxdb.destroy();
  }

  /** Publishes an `AthenaEvent` to all registered subscribers. */
  publish(athenaEvent: AthenaEvent<any, any>): void {
    this.ensureStarted(this.rxdb);
    this.rxdb.athena_events.insert(
      fromAthenaEvent(AthenaEventStream.cloneAthenaEvent(athenaEvent)),
    );
  }

  /**
   * Registers a subscriber. `AthenaEvent`s published before the call to
   * `registerSubscriber` are not processed by the subscriber (from its
   * point of view, it's like they never existed). Only `AthenaEvent`s published
   * after the call to `registerSubscriber` are processed by the subscriber.
   */
  registerSubscriber<
    AthenaEventSubtype extends AthenaEvent<any, any> = AthenaEvent<any, any>,
  >(subscriber: Subscriber<AthenaEventSubtype>): AthenaEventStream {
    if (this.insertSubscriptionsBySubscriberId.has(subscriber.id)) {
      throw new SubscriberAlreadyRegistered(subscriber.id);
    }
    // This might cause problems if we want some subscribers to register before
    // `AthenaEventStream` completed starting. If that's the case, we can modify
    // the registration logic to queue the subscriber for registration (in case
    // `AthenaEventStream` hasn't completed starting) and register it after
    // starting completed.
    this.ensureStarted(this.rxdb);
    const insertSubscription = this.rxdb.athena_events.insert$.subscribe(
      (insertEvent) => {
        this.processAthenaEvent(
          AthenaEventStream.cloneAthenaEvent(
            toAthenaEvent(insertEvent.documentData),
          ),
          subscriber,
        );
      },
    );
    this.insertSubscriptionsBySubscriberId.set(
      subscriber.id,
      insertSubscription,
    );
    return this;
  }

  /**
   * Un-registers a subscriber. `AthenaEvent`s published after the call to
   * `unregisterSubscriber` are not processed by the subscriber anymore.
   */
  unregisterSubscriber(subscriberId: string): AthenaEventStream {
    const insertSubscription =
      this.insertSubscriptionsBySubscriberId.get(subscriberId);
    if (!insertSubscription) {
      throw new SubscriberNotRegistered(subscriberId);
    }
    this.insertSubscriptionsBySubscriberId.delete(subscriberId);
    insertSubscription.unsubscribe();
    return this;
  }

  private processAthenaEvent(
    athenaEvent: AthenaEvent<any, any>,
    subscriber: Subscriber<any>,
  ) {
    try {
      if (
        !AthenaEventStream.athenaEventMatchesFilter(
          athenaEvent,
          subscriber.athenaEventTypeFilter,
        )
      ) {
        return;
      }
      subscriber.processAthenaEvent(athenaEvent, this);
    } catch (error) {
      this.hooks?.onSubscriberProcessAthenaEventError?.(
        new SubscriberProcessAthenaEventError(
          error,
          athenaEvent,
          subscriber.id,
        ),
      );
    }
  }

  /** Finds `AthenaEvent`s by the supplied query. */
  async find(
    selector: MangoQuerySelector<AthenaEvent<any, any>> = {},
  ): Promise<AthenaEvent<any, any>[]> {
    this.ensureStarted(this.rxdb);
    const docs = await this.rxdb.athena_events
      .find({ selector, sort: [{ createdAt: "asc" }] })
      .exec();
    return docs.map(toAthenaEvent);
  }

  /**
   * Returns an rxjs Observable emitting the latest result of the supplied query
   * (an array of `AthenaEvent`s).
   */
  find$(
    selector: MangoQuerySelector<AthenaEvent<any, any>> = {},
  ): Observable<AthenaEvent<any, any>[]> {
    this.ensureStarted(this.rxdb);
    return this.rxdb.athena_events
      .find({ selector, sort: [{ createdAt: "asc" }] })
      .$.pipe(
        map((athenaEventDocuments) =>
          athenaEventDocuments.map((athenaEventDocument) =>
            toAthenaEvent(athenaEventDocument.toJSON()),
          ),
        ),
      );
  }

  /**
   * Returns a promise that resolves:
   *
   * - Right-away if the last performed sync was done less than `delta`
   *   milliseconds ago.
   * - Whenever another sync is performed otherwise.
   */
  async waitForRecentSync(delta: Milliseconds): Promise<void> {
    this.ensureStarted(this.rxdb);
    await firstValueFrom(
      this.rxdb.athena_events
        .getLocal$<LastSyncInfo>(AthenaEventStream.lastSyncInfoId)
        .pipe(
          filter(
            (lastSync) =>
              lastSync !== null &&
              lastSync.get("lastSyncedAt") > Date.now() - delta,
          ),
        ),
    );
  }

  private static lastSyncInfoId = "lastSyncInfo";

  private static athenaEventMatchesFilter(
    athenaEvent: AthenaEvent<any, any>,
    filter:
      | string
      | string[]
      | ((athenaEventType: string) => boolean)
      | undefined,
  ): boolean {
    if (typeof filter === "string") {
      return filter === athenaEvent.type;
    }
    if (Array.isArray(filter)) {
      return filter.includes(athenaEvent.type);
    }
    if (typeof filter === "function") {
      return filter(athenaEvent.type);
    }
    return true;
  }

  private static cloneAthenaEvent(
    athenaEvent: AthenaEvent<any, any>,
  ): AthenaEvent<any, any> {
    return JSON.parse(JSON.stringify(athenaEvent));
  }

  private async createDatabase() {
    this.__rxdb = this.rxdb = await createRxDatabase<{
      athena_events: AthenaEventsCollection;
    }>({
      // `AthenaEventStream` stores events in IndexedDB. The user id is used to
      // define the IndexedDB database name in order to support multiple users
      // using the application on the same browser.
      name: `db_${this.userId}`,
      storage:
        process.env["NODE_ENV"] === "test"
          ? getRxStorageMemory()
          : getRxStorageDexie(),
      eventReduce: true,
      localDocuments: true,
    });
    await this.rxdb.addCollections({
      athena_events: {
        schema: athenaEventSchemaV0,
        localDocuments: true,
      },
    });
  }

  private async startReplication() {
    this.ensureStarted(this.rxdb);

    const replicationState = replicateRxCollection<
      RxdbAthenaEvent,
      ReplicationCheckpoint
    >({
      collection: this.rxdb.athena_events,
      replicationIdentifier: [
        "athena_events_replication",
        this.userId,
        this.eventStorageClient.eventStorageOrigin,
        this.pullAthenaEventTypes?.join("_") ?? "ALL",
      ].join("_"),
      retryTime: this.retryFailedPushOrPullAfter,

      push: {
        // If pushing a batch fails, when RxDB retries the push, it pushes again
        // the entire batch. On the other hand, when we try to save a batch of
        // `AthenaEvent`s to `EvidenceB/event-storage`, some `AthenaEvent`s
        // might get saved successfully, while others don't. In that case there
        // would be no way of telling RxDB to only retry pushing those
        // `AthenaEvent`s that failed. This would be problematic in the case in
        // which a batch contains an `AthenaEvent` that fails saving due to a
        // non-retryable error. Subsequent attempts at saving the batch would
        // keep failing because of that "rogue" `AthenaEvent`, and the whole
        // batch would never be marked as replicated by RxDB.
        batchSize: 1,
        handler: async (docs) => {
          if (docs.length !== 1) {
            const error = new ReplicationPushWrongBatchSizeError(docs.length);
            this.hooks?.onReplicationPushWrongBatchSizeError?.(error);
            throw error;
          }
          const [doc] = docs as [
            RxReplicationWriteToMasterRow<RxdbAthenaEvent>,
          ];
          const [result] = (await this.eventStorageClient.saveAthenaEvents([
            toAthenaEvent(doc.newDocumentState),
          ])) as [AthenaEventSaveResult];
          if (result.error) {
            if (result.error.retryable) {
              const error = new ReplicationPushRetryableError(result);
              this.hooks?.onReplicationPushRetryableError?.(error);
              throw error;
            } else {
              const error = new ReplicationPushNonRetryableError(result);
              this.hooks?.onReplicationPushNonRetryableError?.(error);
              // It's pointless to retry saving `AthenaEvent`s that failed
              // saving with a non-retryable errors. Hence we tell RxDB to mark
              // the `AthenaEvent` as replicated and stop trying to save it.
              return [];
            }
          }
          return [];
        },
      },

      pull: {
        // batchSize is set to be very large, so we expect all new
        // `AthenaEvent`s to be pulled at once.
        batchSize: 100_000,
        handler: async (lastCheckpoint) => {
          try {
            const athenaEvents =
              await this.eventStorageClient.fetchAthenaEvents({
                userId: this.userId,
                athenaEventTypes: this.pullAthenaEventTypes,
                insertedAfter: lastCheckpoint
                  ? new Date(lastCheckpoint.insertedAt)
                  : undefined,
              });
            const lastInserted = last(sortBy(athenaEvents, "insertedAt", "id"));
            return {
              documents: athenaEvents.map(fromAthenaEvent),
              checkpoint: lastInserted
                ? {
                    id: lastInserted.id,
                    insertedAt: lastInserted.insertedAt!,
                  }
                : (lastCheckpoint ?? null),
            };
          } catch (cause: any) {
            const error = new ReplicationPullError(cause);
            this.hooks?.onReplicationPullError?.(error);
            throw error;
          }
        },
        // Sets an interval to periodically pull new `AthenaEvent`s.
        stream$: interval(this.pullInterval).pipe(map(() => "RESYNC")),
      },
      // We start the replication manually just a few lines above, to ensure
      // that the lastSync local doc is set up before it starts.
      autoStart: false,
    });

    // Create a local document keeping track of when he last sync was performed,
    // and update it on each sync.
    await this.rxdb.athena_events
      .insertLocal<LastSyncInfo>(AthenaEventStream.lastSyncInfoId, {
        // Set to any date in the past (epoch)
        lastSyncedAt: 0,
      })
      .catch(() => {
        // Ignore error thrown if the local document already exists.
      });
    replicationState.active$
      .pipe(
        mergeMap(async () => {
          await replicationState.awaitInSync();
          await this.rxdb!.athena_events.upsertLocal<LastSyncInfo>(
            AthenaEventStream.lastSyncInfoId,
            { lastSyncedAt: Date.now() },
          );
        }),
      )
      .subscribe();

    await replicationState.start();

    this.__replicationState = replicationState;
  }

  private ensureStarted(rxdb: Database | null): asserts rxdb is Database {
    if (!rxdb) {
      throw new AthenaEventStreamNotStarted();
    }
  }
}
